import React, { useState, useEffect } from 'react';
import { investmentAPI } from '../services/api';
import { 
  CheckIcon, 
  SparklesIcon, 
  ShieldCheckIcon, 
  BoltIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  CalendarIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    fetchPlans();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await investmentAPI.getPlans();
      
      if (response.data && Array.isArray(response.data)) {
        setPlans(response.data);
      } else {
        setError('Invalid data format received');
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      setError(error.message || 'Failed to fetch plans');
    } finally {
      setLoading(false);
    }
  };

  const handleBuyPlan = async (planId) => {
    try {
      setBuying(true);
      setSelectedPlanId(planId);
      await investmentAPI.buyPlan(planId);
      alert('🎉 Plan purchased successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to purchase plan');
    } finally {
      setBuying(false);
      setSelectedPlanId(null);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return '$0';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const features = [
    { icon: '🛡️', text: '24/7 Support' },
    { icon: '🔒', text: 'Secure Investment' },
    { icon: '💰', text: 'Daily Payouts' },
    { icon: '📊', text: 'Flexible Plans' },
    { icon: '👥', text: 'Referral Bonus' },
    { icon: '✨', text: 'No Hidden Fees' }
  ];

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        gap: '20px',
        backgroundColor: '#0a0a0a'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid rgba(0, 212, 255, 0.2)',
          borderTop: '4px solid #00d4ff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p style={{ color: '#94a3b8' }}>Loading investment plans...</p>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        gap: '20px',
        padding: '20px',
        backgroundColor: '#0a0a0a'
      }}>
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '12px',
          padding: '16px 24px',
          color: '#f87171',
          textAlign: 'center'
        }}>
          {error}
        </div>
        <button 
          onClick={fetchPlans}
          style={{
            padding: '12px 24px',
            background: '#00d4ff',
            border: 'none',
            borderRadius: '12px',
            color: '#1a1a2e',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!plans || plans.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '60px 20px',
        maxWidth: '1200px',
        margin: '0 auto',
        backgroundColor: '#0a0a0a',
        minHeight: '100vh'
      }}>
        <div style={{ fontSize: '64px', marginBottom: '16px' }}>📊</div>
        <h3 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: 'white',
          marginBottom: '8px'
        }}>No Investment Plans Available</h3>
        <p style={{ color: '#94a3b8', marginBottom: '20px' }}>
          New plans will be added soon. Check back later!
        </p>
        <button 
          onClick={fetchPlans}
          style={{
            padding: '12px 24px',
            background: '#00d4ff',
            border: 'none',
            borderRadius: '12px',
            color: '#1a1a2e',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Refresh
        </button>
      </div>
    );
  }

  return (
    <div style={{
      padding: isMobile ? '16px' : '20px',
      maxWidth: '1400px',
      margin: '0 auto',
      paddingBottom: isMobile ? '80px' : '20px',
      backgroundColor: '#0a0a0a',
      minHeight: '100vh'
    }}>
      {/* Header - Dark Theme */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
        borderRadius: '24px',
        padding: '32px 24px',
        color: 'white',
        marginBottom: '24px',
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid rgba(0, 212, 255, 0.3)',
        boxShadow: '0 10px 30px rgba(0, 212, 255, 0.2)'
      }}>
        <div style={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(0, 212, 255, 0.1), transparent)',
          borderRadius: '50%'
        }} />
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '16px',
          marginBottom: '20px'
        }}>
          <div>
            <h1 style={{
              fontSize: isMobile ? '28px' : '36px',
              fontWeight: '700',
              margin: '0 0 8px 0',
              background: 'linear-gradient(135deg, #fff, #00d4ff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>Investment Plans</h1>
            <p style={{
              opacity: '0.7',
              margin: 0,
              fontSize: '16px'
            }}>Choose your perfect plan</p>
          </div>
          <SparklesIcon style={{ width: '40px', height: '40px', opacity: '0.8', color: '#00d4ff' }} />
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          background: 'rgba(0, 212, 255, 0.1)',
          borderRadius: '12px',
          padding: '12px 16px',
          marginTop: '16px',
          border: '1px solid rgba(0, 212, 255, 0.2)'
        }}>
          <ShieldCheckIcon style={{ width: '20px', height: '20px', color: '#00d4ff' }} />
          <p style={{
            fontSize: '14px',
            margin: 0,
            opacity: '0.9'
          }}>All plans are secured and insured</p>
        </div>
      </div>

      {/* Features - Dark Theme */}
      <div style={{
        background: '#1a1a2e',
        borderRadius: '24px',
        border: '1px solid rgba(0, 212, 255, 0.2)',
        padding: '24px',
        marginBottom: '32px'
      }}>
        <h2 style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: '20px',
          fontWeight: '600',
          color: '#00d4ff',
          marginBottom: '20px'
        }}>
          <BoltIcon style={{ width: '24px', height: '24px', color: '#00d4ff' }} />
          Why Invest With Us?
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
          gap: '16px'
        }}>
          {features.map((feature, index) => (
            <div key={index} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '14px',
              color: '#cbd5e1'
            }}>
              <span style={{ fontSize: '20px' }}>{feature.icon}</span>
              <span>{feature.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Plans Grid - Dark Theme */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(350px, 1fr))',
        gap: '24px'
      }}>
        {plans.map((plan) => {
          const price = parseFloat(plan?.price) || 0;
          const dailyIncome = parseFloat(plan?.dailyIncome) || 0;
          const totalAmount = parseFloat(plan?.totalAmount) || 0;
          const cycle = parseInt(plan?.cycle) || 180;
          
          const dailyROI = price > 0 ? ((dailyIncome / price) * 100).toFixed(2) : '0.00';
          const totalROI = price > 0 ? ((totalAmount / price) * 100).toFixed(2) : '0.00';
          
          const isPopular = plan.popular || plan.name?.includes('Popular') || price === 500;
          
          return (
            <div 
              key={plan.id} 
              style={{
                position: 'relative',
                background: '#1a1a2e',
                borderRadius: '24px',
                border: isPopular ? '2px solid #00d4ff' : '1px solid rgba(0, 212, 255, 0.2)',
                overflow: 'hidden',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 212, 255, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {isPopular && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  background: '#00d4ff',
                  color: '#1a1a2e',
                  padding: '6px 16px',
                  fontSize: '12px',
                  fontWeight: '600',
                  borderRadius: '0 0 0 12px'
                }}>
                  ⭐ MOST POPULAR
                </div>
              )}
              
              <div style={{ padding: '24px' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '24px',
                  flexWrap: 'wrap',
                  gap: '16px'
                }}>
                  <div>
                    <h3 style={{
                      fontSize: '24px',
                      fontWeight: '700',
                      color: '#00d4ff',
                      marginBottom: '8px'
                    }}>{plan.name || 'Investment Plan'}</h3>
                    <p style={{
                      fontSize: '14px',
                      color: '#94a3b8',
                      margin: 0
                    }}>6-month investment plan</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      fontSize: '32px',
                      fontWeight: '800',
                      color: 'white'
                    }}>
                      {formatCurrency(price)}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#94a3b8'
                    }}>Investment</div>
                  </div>
                </div>

                {/* Plan Details - Dark Theme */}
                <div style={{
                  background: '#0f0f0f',
                  borderRadius: '16px',
                  padding: '16px',
                  marginBottom: '24px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 0',
                    borderBottom: '1px solid rgba(0, 212, 255, 0.1)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <CurrencyDollarIcon style={{ width: '16px', height: '16px', color: '#00d4ff' }} />
                      <span style={{ color: '#94a3b8' }}>Daily Income</span>
                    </div>
                    <span style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#00d4ff'
                    }}>
                      ${dailyIncome.toFixed(2)}
                    </span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 0',
                    borderBottom: '1px solid rgba(0, 212, 255, 0.1)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <CalendarIcon style={{ width: '16px', height: '16px', color: '#00d4ff' }} />
                      <span style={{ color: '#94a3b8' }}>Cycle Duration</span>
                    </div>
                    <span style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: 'white'
                    }}>
                      {cycle} days
                    </span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 0',
                    borderBottom: '1px solid rgba(0, 212, 255, 0.1)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <ArrowTrendingUpIcon style={{ width: '16px', height: '16px', color: '#f59e0b' }} />
                      <span style={{ color: '#94a3b8' }}>Total Return</span>
                    </div>
                    <span style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#f59e0b'
                    }}>
                      {formatCurrency(totalAmount)}
                    </span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 0',
                    borderBottom: '1px solid rgba(0, 212, 255, 0.1)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <ChartBarIcon style={{ width: '16px', height: '16px', color: '#00d4ff' }} />
                      <span style={{ color: '#94a3b8' }}>Daily ROI</span>
                    </div>
                    <span style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#00d4ff'
                    }}>
                      {dailyROI}%
                    </span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 0'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <SparklesIcon style={{ width: '16px', height: '16px', color: '#00d4ff' }} />
                      <span style={{ color: '#94a3b8' }}>Total ROI</span>
                    </div>
                    <span style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#00d4ff'
                    }}>
                      {totalROI}%
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => handleBuyPlan(plan.id)}
                  disabled={buying}
                  style={{
                    width: '100%',
                    padding: '16px',
                    background: isPopular 
                      ? '#00d4ff'
                      : '#00d4ff',
                    border: 'none',
                    borderRadius: '16px',
                    color: '#1a1a2e',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: buying ? 'not-allowed' : 'pointer',
                    opacity: buying && selectedPlanId === plan.id ? 0.7 : 1,
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (!buying) e.currentTarget.style.background = '#00e5ff';
                  }}
                  onMouseLeave={(e) => {
                    if (!buying) e.currentTarget.style.background = '#00d4ff';
                  }}
                >
                  {buying && selectedPlanId === plan.id ? (
                    <>
                      <div style={{
                        width: '20px',
                        height: '20px',
                        border: '2px solid #1a1a2e',
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      Processing...
                    </>
                  ) : (
                    `Invest ${formatCurrency(price)}`
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        body {
          background-color: #0a0a0a;
        }
      `}</style>
    </div>
  );
};

export default Plans;